<script setup lang="ts">
/**
 * A record the agent made or touched (draft, invoice, contract, project…), as a compact card in the
 * conversation. Clicking opens it in the conversation's workspace pane (a bottom sheet on phones), or over the
 * conversation for a contract review, project, deliverable or task (see `artifactSurface`); brands and contacts open nothing.
 *
 * An email draft is the one thing that still needs the creator (agents never send email), so its chip
 * says so: "Ready to send", with a Review & send cue that opens the composer, until it's sent.
 *
 * A new contract review the creator hasn't opened yet glows (a violet light running around its border) to pull
 * them in; it stops for good once they open it (`markReviewSeen`).
 *
 * Phones (`chatBubbles`): a glass card like the agent's bubbles that presses in on touch; a long press opens an
 * action sheet (Open, Open full page, Share link). Hover lifts only happen with a mouse.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { ARTIFACT_KINDS, artifactKey, artifactSurface, type ArtifactRef } from '../artifacts'
import { artifactMenu, recordRoute, shareArtifactLink } from '../artifactActions'
import { openArtifact } from '../openArtifact'
import { useCardMode } from '../cardMode'
import { useOverlays } from '../overlays'
import { isDraftSent, isReviewSeen, markReviewSeen, useWorkspace } from '../workspace'
import { usePressAndHold } from '@/composables/usePressAndHold'
import { actionSheet } from '@/components/mobile/actionSheet'
import { vPress } from '@/directives/press'
import useToast from '@/composables/useToast'

const props = defineProps<{ artifact: ArtifactRef }>()

const { phone: bubbles } = useCardMode()
const ws = useWorkspace()
const overlays = useOverlays()
let router: ReturnType<typeof useRouter> | null = null
try { router = useRouter() } catch { router = null }

const kind = computed(() => ARTIFACT_KINDS[props.artifact.type])
const isOpen = computed(() => ws?.activeKey === artifactKey(props.artifact))
/** A single email draft: waiting for the creator to send it, or already sent. */
const draft = computed<'ready' | 'sent' | null>(() => {
    const a = props.artifact
    if (a.type !== 'draft' || a.items) return null
    return isDraftSent(a.id) || a.data?.sent_by ? 'sent' : 'ready'
})
const meta = computed(() => {
    const a = props.artifact
    if (draft.value) return [draft.value === 'sent' ? 'Sent' : 'Ready to send', a.subtitle].filter(Boolean).join(' · ')
    const verb = a.verb === 'created' ? `New ${kind.value.label.toLowerCase()}` : a.verb === 'updated' ? `${kind.value.label} · updated` : kind.value.label
    return a.items ? a.subtitle : [verb, a.subtitle].filter(Boolean).join(' · ')
})

/** A new contract review the creator hasn't opened: it glows until they do. */
const glowing = computed(() => {
    const a = props.artifact
    return a.type === 'agreement' && a.verb === 'created' && !a.items && !isOpen.value && !isReviewSeen(a.id)
})

/** Brands and contacts are shown as context: they open nothing. */
const opens = computed(() => artifactSurface(props.artifact) !== 'none')

function open() {
    if (props.artifact.type === 'agreement') markReviewSeen(props.artifact.id)
    openArtifact(props.artifact, { ws, overlays, router })
}

// ── phones: long press for more ──
const menu = computed(() => (bubbles.value ? artifactMenu(props.artifact) : []))
const hold = usePressAndHold(() => void showMenu(), { haptic: true, disabled: () => !menu.value.length })
async function showMenu() {
    const a = props.artifact
    const choice = await actionSheet({ title: a.title, message: meta.value || undefined, actions: menu.value })
    if (choice === 'open') open()
    else if (choice === 'page') {
        const path = recordRoute(a)
        if (path) void router?.push(path)
    } else if (choice === 'share') {
        const out = await shareArtifactLink(a)
        if (out === 'copied') useToast().showToast({ type: 'success', message: 'Link copied' })
        else if (out === 'failed') useToast().showToast({ type: 'error', message: "Couldn't share that link." })
    }
}
</script>

<template>
    <component
        :is="opens ? 'button' : 'div'"
        v-press="{ variant: 'card', disabled: !bubbles || (!opens && !menu.length) }"
        :type="opens ? 'button' : undefined"
        class="artifact-chip"
        :class="[`tone-${kind.tone}`, { 'is-open': isOpen, 'is-static': !opens, 'is-bubble': bubbles, 'is-glowing': glowing }]"
        :data-artifact="artifactKey(artifact)"
        data-testid="artifact-chip"
        v-bind="hold.handlers"
        @click="opens && open()"
    >
        <span class="tile" aria-hidden="true">
            <i class="fa-regular" :class="kind.icon" />
        </span>
        <span class="body">
            <span class="chip-title">{{ artifact.title }}</span>
            <span v-if="meta" class="meta">{{ meta }}</span>
        </span>
        <span v-if="draft === 'ready'" class="cta" data-testid="draft-review-send">Review &amp; send</span>
        <span v-else-if="opens" class="go" aria-hidden="true">
            <svg viewBox="0 0 16 16"><path d="M6 4l4 4-4 4" /></svg>
        </span>
    </component>
</template>

<style scoped>
.artifact-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    min-width: 0;
    min-height: 56px;
    padding: 9px 12px 9px 9px;
    border-radius: 14px;
    text-align: left;
    background: var(--agent-card);
    border: 1px solid var(--agent-card-border);
    box-shadow: 0 1px 2px rgb(15 15 15 / 0.04);
    cursor: pointer;
    transition:
        transform 260ms var(--agent-spring),
        box-shadow 220ms var(--agent-ease),
        border-color 220ms var(--agent-ease);
    -webkit-tap-highlight-color: transparent;
}
@media (hover: hover) and (pointer: fine) {
    .artifact-chip:hover {
        transform: translateY(-1px);
        border-color: rgb(15 15 15 / 0.11);
        box-shadow: var(--agent-card-shadow-hover);
    }
    html.dark .artifact-chip:hover { border-color: rgb(255 255 255 / 0.14); }
    .artifact-chip:hover .tile { transform: scale(1.06) rotate(-3deg); }
    .artifact-chip:hover .go { transform: translateX(2px); color: var(--agent-text-2); }
    .artifact-chip:hover .cta { transform: translateX(1px); filter: brightness(1.05); }
    .artifact-chip.is-static:hover { transform: none; border-color: var(--agent-card-border); box-shadow: 0 1px 2px rgb(15 15 15 / 0.04); }
    .artifact-chip.is-static:hover .tile { transform: none; }
}
.artifact-chip:not(.is-bubble):active { transform: scale(0.985); }
.artifact-chip.is-open {
    border-color: var(--agent-accent-ring);
    box-shadow: 0 0 0 3px var(--agent-accent-soft), var(--agent-card-shadow);
}
/* Brands and contacts: context, not a control. */
.artifact-chip.is-static { cursor: default; }
.artifact-chip.is-static:not(.is-bubble):active { transform: none; }

/* Phones: pale glass like the agent's bubbles, a touch taller, no text selection under a long press. */
.artifact-chip.is-bubble {
    min-height: 60px;
    padding: 10px 14px 10px 10px;
    border: 0;
    border-radius: 18px;
    background-color: rgba(255, 255, 255, 0.78);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0.1));
    box-shadow:
        0 0 0 0.5px rgba(36, 36, 36, 0.08),
        0 6px 18px rgba(36, 36, 36, 0.07),
        inset 1px 1px 1px -0.5px rgba(255, 255, 255, 1);
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
}
html.dark .artifact-chip.is-bubble {
    background-color: rgba(68, 64, 60, 0.6);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
    box-shadow: 0 0 0 0.5px rgba(255, 255, 255, 0.12), 0 6px 18px rgba(0, 0, 0, 0.35), inset 1px 1px 1px -0.5px rgba(255, 255, 255, 0.08);
}
.artifact-chip.is-bubble.is-open {
    box-shadow: 0 0 0 1.5px var(--agent-accent-ring), 0 6px 18px rgba(36, 36, 36, 0.07);
}
.artifact-chip.is-bubble .tile { width: 40px; height: 40px; border-radius: 12px; font-size: 15px; }
.artifact-chip.is-bubble .chip-title { font-size: 15px; line-height: 20px; }
.artifact-chip.is-bubble .meta { font-size: 13px; line-height: 17px; }
.artifact-chip.is-bubble .cta { padding: 8px 12px; font-size: 13px; }

/* A new contract review: a violet light runs around the border (a conic gradient masked to a ring) while a
   soft halo breathes behind it. Reduced motion keeps the lit border, still. */
@property --chip-glow-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
}
.artifact-chip.is-glowing {
    border-color: rgb(139 92 246 / 0.22);
    animation: chip-glow-halo 2.8s ease-in-out infinite;
}
.artifact-chip.is-glowing::before {
    content: '';
    position: absolute;
    inset: -1px;
    padding: 1.5px;
    border-radius: inherit;
    background: conic-gradient(
        from var(--chip-glow-angle),
        transparent 0deg,
        rgb(139 92 246 / 0.15) 80deg,
        #8b5cf6 150deg,
        #d946ef 185deg,
        #a78bfa 215deg,
        rgb(139 92 246 / 0.15) 280deg,
        transparent 360deg
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    animation: chip-glow-spin 3.2s linear infinite;
}
.artifact-chip.is-glowing .tile { animation: chip-glow-tile 2.8s ease-in-out infinite; }
@keyframes chip-glow-spin {
    to { --chip-glow-angle: 360deg; }
}
@keyframes chip-glow-halo {
    0%, 100% { box-shadow: 0 0 0 0 rgb(139 92 246 / 0), 0 2px 10px -2px rgb(139 92 246 / 0.18); }
    50% { box-shadow: 0 0 0 4px rgb(139 92 246 / 0.08), 0 4px 22px -2px rgb(139 92 246 / 0.38); }
}
@keyframes chip-glow-tile {
    0%, 100% { box-shadow: 0 0 0 0 rgb(139 92 246 / 0); }
    50% { box-shadow: 0 0 0 3px rgb(139 92 246 / 0.12); }
}
html.dark .artifact-chip.is-glowing { border-color: rgb(167 139 250 / 0.3); }
@media (prefers-reduced-motion: reduce) {
    .artifact-chip.is-glowing,
    .artifact-chip.is-glowing::before,
    .artifact-chip.is-glowing .tile { animation: none; }
    .artifact-chip.is-glowing { box-shadow: 0 0 0 3px rgb(139 92 246 / 0.1), 0 4px 18px -2px rgb(139 92 246 / 0.3); }
}

.tile {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 14px;
    transition: transform 320ms var(--agent-spring);
}
.tone-orange .tile { background: rgb(251 76 31 / 0.1); color: #e2400f; }
.tone-blue .tile { background: rgb(59 130 246 / 0.1); color: #2563eb; }
.tone-emerald .tile { background: rgb(16 185 129 / 0.12); color: #059669; }
.tone-violet .tile { background: rgb(139 92 246 / 0.11); color: #7c3aed; }
.tone-amber .tile { background: rgb(245 158 11 / 0.13); color: #d97706; }
.tone-neutral .tile { background: var(--agent-hover); color: var(--agent-text-2); }

.body {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    gap: 1px;
}
.chip-title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13.5px;
    font-weight: 550;
    line-height: 19px;
    color: var(--agent-text-1);
}
.meta {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    line-height: 16px;
    color: var(--agent-text-3);
}

.go {
    display: flex;
    flex-shrink: 0;
    color: var(--agent-text-4);
    transition: transform 260ms var(--agent-spring), color 160ms ease;
}
.go svg {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.cta {
    flex-shrink: 0;
    padding: 6px 11px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
    color: #fff;
    background: var(--agent-accent);
    transition: transform 260ms var(--agent-spring), filter 160ms ease;
}
</style>
